import * as React from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tooltip'
import { cn } from '@/lib/utils'

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className='relative w-full overflow-auto'>
    <table
      ref={ref}
      className={cn('w-full caption-bottom text-sm', className)}
      {...props}
    />
  </div>
))
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />
))
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
))
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'bg-muted/50 border-t font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    rowContrasting?: boolean
    isTotalRow?: boolean
  }
>(({ className, rowContrasting, isTotalRow, ...props }, ref) => {
  return (
    <tr
      ref={ref}
      className={cn(
        `border-background hover:bg-muted/50 data-[state=selected]:bg-muted border-b-4 transition-colors ${
          isTotalRow
            ? 'bg-background'
            : rowContrasting
              ? 'even:bg-muted odd:bg-muted-alt'
              : ''
        }`,
        className
      )}
      {...props}
    />
  )
})
TableRow.displayName = 'TableRow'

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    arms?: boolean
  }
>(({ arms, className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'text-foreground relative h-14 px-2 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  >
    {props.children}
    {arms && (
      <>
        <div className='bg-foreground absolute top-1/2 left-0 h-px w-2/5'>
          <div className='bg-foreground absolute top-0 left-0 h-2 w-px'></div>
        </div>
        <div className='bg-foreground absolute top-1/2 right-0 h-px w-2/5'>
          <div className='bg-foreground absolute top-0 right-0 h-2 w-px'></div>
        </div>
      </>
    )}
  </th>
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'border-background border-r-4 p-3 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableMetricCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    // Numeric values drive the bar geometry only. The tooltip renders the
    // pre-formatted `*Label` strings supplied by the caller — this component
    // holds no formatting opinion (decimals/units are the caller's concern).
    value: number
    maxValue: number
    markerValue: number
    valueLabel?: string
    maxLabel?: string
    markerLabel?: string
  }
>(
  (
    {
      className,
      value,
      maxValue,
      markerValue,
      valueLabel,
      maxLabel,
      markerLabel,
      ...props
    },
    ref
  ) => {
    const barProps = React.useMemo(() => {
      let markerPercent: number | null = null,
        barColor: string | null = null,
        valuePercent: number | null = null

      if (!value || !maxValue)
        return {
          valuePercent,
          markerPercent,
          barColor
        }

      valuePercent = (value / maxValue) * 100

      if (markerValue) {
        markerPercent = (markerValue / maxValue) * 100

        // Determine color based on value compared to average
        barColor = 'bg-yellow-500'

        if (value >= markerValue) {
          barColor = 'bg-green-500'
        }
        const percentBelowAverage = ((markerValue - value) / markerValue) * 100
        if (percentBelowAverage > 50) {
          barColor = 'bg-red-500'
        }
      }

      return {
        valuePercent,
        markerPercent,
        barColor
      }
    }, [value, maxValue, markerValue])

    const hasTooltip = value != null && maxValue != null && maxValue > 0

    const cell = (
      <td
        ref={ref}
        className={cn(
          'border-background relative border-r-4 p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
          className
        )}
        {...props}
      >
        {props.children}
        {barProps.valuePercent && (
          <div className='bg-muted absolute bottom-0 left-0 h-1 w-full'>
            <div
              className={`absolute top-0 left-0 h-full ${barProps.barColor}`}
              style={{ width: `${barProps.valuePercent}%` }}
            />
            {barProps.markerPercent && (
              <div
                className='bg-foreground/80 absolute top-0 h-full w-2'
                style={{
                  // Prevent pushing out of wrapper bar
                  left: `${barProps.markerPercent < 95 ? barProps.markerPercent : 95}%`
                }}
              />
            )}
          </div>
        )}
      </td>
    )

    if (!hasTooltip) return cell

    return (
      <Tooltip>
        <TooltipTrigger asChild>{cell}</TooltipTrigger>
        <TooltipContent variant='popover'>
          <div className='text-xs'>
            <div>Value: {valueLabel ?? value}</div>
            {markerValue != null && (
              <div>Season Avg: {markerLabel ?? markerValue}</div>
            )}
            <div>Max: {maxLabel ?? maxValue}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    )
  }
)
TableMetricCell.displayName = 'TableMetricCell'

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-muted-foreground mt-4 text-sm', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableMetricCell,
  TableRow
}
